import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import useProgressDelivery from "../../../hooks/useProgressDelivery";
import {
  authSelectFfid,
  authSelectFistName,
} from "../../../reduxFolder/selectors/globalSelectors";
import BooksHeader from "../../Books/components/BooksHeader/BooksHeader";
import RecipientsHeader from "../../Books/components/RecipientsHeader/RecipientsHeader";
import DeliveryHeading from "../../Delivery/components/DeliveryHeading/DeliveryHeading";
import InboundHeader from "../../Inbound/components/InboundHeader/InboundHeader";
import MailboxHeader from "../../Mailbox/components/MailboxHeader";
import ParcelHeader from "../../Parcels/components/ParcelHeader";
import ShopifyHeader from "../../Shopify/components/ShopifyHeader/ShopifyHeader";
import ToolsHeader from "../../Tools/components/ToolsHeader";
import UtilitiesHeader from "../../Utilities/components/UtilitiesHeader/UtilitiesHeader";
import { HeaderTitle } from "../HeaderContainer.styles";

type ComponentKeys =
  | "tools"
  | "books"
  | "parcels"
  | "shopify"
  | "mailbox"
  | "inbound"
  | "utilities";

const componentsMap: Record<ComponentKeys, () => JSX.Element> = {
  tools: () => <ToolsHeader />,
  books: () => <BooksHeader />,
  parcels: () => <ParcelHeader />,
  shopify: () => <ShopifyHeader />,
  mailbox: () => <MailboxHeader />,
  inbound: () => <InboundHeader />,
  utilities: () => <UtilitiesHeader />,
};

interface BasicHeaderProps {
  userFfid: string;
  firstName: string;
}

const BasicHeader: FC<BasicHeaderProps> = React.memo(
  ({ userFfid, firstName }) => {
    const location = useLocation();
    const [access] = useProgressDelivery(userFfid);
    const { t } = useTranslation("common");
    const pathSegments = location.pathname.split("/").filter(Boolean);
    const path = pathSegments[0] ?? "";
    const isRecipientsSection =
      path === "books" &&
      (pathSegments[1] === "recipients" || pathSegments[1] === "passports");

    const title = isRecipientsSection
      ? t("pages.recipients")
      : t(`pages.${path || "home"}`);
    const HeaderComponent = isRecipientsSection
      ? <RecipientsHeader />
      : (componentsMap[path as ComponentKeys]?.() ?? null);

    if (!path) {
      return (
        <Wrapper>
          <HeaderTitle>
            {firstName ? t("account.hiUser", { fullName: firstName }) : ""}
          </HeaderTitle>
        </Wrapper>
      );
    }
    if (path === "home") return <Wrapper />;
    if (path === "shipping") return <DeliveryHeading access={access} />;

    return (
      <Wrapper>
        <HeaderTitle>{title}</HeaderTitle>
        {HeaderComponent}
      </Wrapper>
    );
  },
);

export const Wrapper = styled.span`
  display: flex;
  align-items: center;
  width: 100%;

  ${(props) => props.theme.sm`
    width: 100%;
  `}
`;

const withConnect = connect(
  createStructuredSelector({
    userFfid: authSelectFfid,
    firstName: authSelectFistName,
  }),
);

export default withConnect(BasicHeader);
